import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi } from '../lib/api';
import { 
  deriveKeyFromPassword, 
  decryptPrivateKey, 
  storeEncryptionKeys, 
  clearEncryptionKeys,
  hasEncryptionKeys 
} from '../lib/crypto';
import { announceSignedOut } from '../lib/keyShare';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // { code, context: 'register' | 'recover' } — shown once by PendingRecoveryCodeDialog
  const [pendingRecoveryCode, setPendingRecoveryCode] = useState(null);
  const [mfaPending, setMfaPending] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [pendingEncryptionData, setPendingEncryptionData] = useState(null);
  const loadingRef = useRef(false);

  const loadUser = useCallback(async () => {
    // Prevent multiple simultaneous loads
    if (loadingRef.current) return;
    
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setLoading(false);
      setUser(null);
      return;
    }

    loadingRef.current = true;
    try {
      const response = await authApi.getMe();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user:', error);
      // Only clear on 401 errors, not network errors
      if (error.response?.status === 401) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        clearEncryptionKeys();
        setUser(null);
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  /**
   * Process encryption keys from server response.
   * Decrypts the private key client-side using the password.
   */
  const processEncryptionKeys = async (password, responseData) => {
    const { encrypted_private_key, encryption_salt } = responseData;

    const keys = {};

    // The server only ever returns the sealed private key and the salt. The
    // password-derived key is computed here, in the browser, and never
    // travels over the network.
    if (encryption_salt) {
      const pdk = deriveKeyFromPassword(password, encryption_salt);
      // Also kept as the legacy symmetric key for pre-RSA documents.
      keys.encryptionKey = pdk;

      if (encrypted_private_key) {
        try {
          keys.privateKey = await decryptPrivateKey(encrypted_private_key, pdk);
        } catch (error) {
          console.error('Failed to decrypt private key:', error);
          // Fall back to legacy encryption key
        }
      }
    }

    storeEncryptionKeys(keys);
  };

  const register = async (email, password, fullName, inviteToken) => {
    const response = await authApi.register({
      email, password, full_name: fullName,
      ...(inviteToken ? { invite_token: inviteToken } : {}),
    });
    const { access_token, user_id } = response.data;
    
    localStorage.setItem('auth_token', access_token);
    
    // Process encryption keys
    await processEncryptionKeys(password, response.data);
    
    await loadUser();
    // The recovery code is only ever returned here. The route guards redirect
    // as soon as `user` loads, so the dialog is rendered at the app root from
    // this pending state rather than by the Register page.
    if (response.data.recovery_code) {
      setPendingRecoveryCode({ code: response.data.recovery_code, context: 'register' });
    }
    return { userId: user_id, recoveryCode: response.data.recovery_code };
  };

  /**
   * Forgot-password flow. The recovery code unseals the private key server-side,
   * which is re-sealed under the new password. Signs the user in and returns the
   * freshly rotated recovery code (the old one is now dead).
   */
  const recoverAccount = async ({ email, recoveryCode, newPassword, mfaCode }) => {
    const response = await authApi.recover({
      email,
      recovery_code: recoveryCode,
      new_password: newPassword,
      mfa_code: mfaCode || null,
    });
    localStorage.setItem('auth_token', response.data.access_token);
    await processEncryptionKeys(newPassword, response.data);
    if (response.data.recovery_code) {
      setPendingRecoveryCode({ code: response.data.recovery_code, context: 'recover' });
    }
    await loadUser();
    return { recoveryCode: response.data.recovery_code };
  };

  const clearPendingRecoveryCode = () => setPendingRecoveryCode(null);

  const login = async (email, password) => {
    const response = await authApi.login({ email, password });
    const { access_token, user_id, requires_mfa, mfa_enabled } = response.data;
    
    if (requires_mfa && mfa_enabled) {
      localStorage.setItem('auth_token', access_token);
      setMfaPending(true);
      setPendingUserId(user_id);
      // Store encryption data and password temporarily for after MFA
      setPendingEncryptionData({ password, responseData: response.data });
      return { requiresMfa: true, userId: user_id };
    }
    
    localStorage.setItem('auth_token', access_token);
    
    // Process encryption keys
    await processEncryptionKeys(password, response.data);
    
    await loadUser();
    return { requiresMfa: false };
  };

  const verifyMfa = async (code) => {
    const response = await authApi.verifyMfa({ user_id: pendingUserId, code });
    const { access_token } = response.data;
    localStorage.setItem('auth_token', access_token);
    
    // Now process the encryption keys that were pending from login
    if (pendingEncryptionData) {
      await processEncryptionKeys(
        pendingEncryptionData.password, 
        pendingEncryptionData.responseData
      );
      setPendingEncryptionData(null);
    }
    
    setMfaPending(false);
    setPendingUserId(null);
    await loadUser();
  };

  const setupMfa = async () => {
    const response = await authApi.setupMfa();
    return response.data;
  };

  const completeMfaSetup = async (code) => {
    await authApi.verifyMfa({ user_id: user.id, code });
    await loadUser();
  };

  const logout = () => {
    // Every open tab locks too (lib/keyShare.js), before the token goes.
    announceSignedOut();
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    // Clear all encryption keys
    clearEncryptionKeys();
    setUser(null);
    setMfaPending(false);
    setPendingUserId(null);
    setPendingEncryptionData(null);
  };

  const value = {
    user,
    loading,
    mfaPending,
    pendingUserId,
    register,
    recoverAccount,
    pendingRecoveryCode,
    clearPendingRecoveryCode,
    login,
    verifyMfa,
    setupMfa,
    completeMfaSetup,
    logout,
    loadUser,
    hasEncryptionKey: hasEncryptionKeys,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
